import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Skeleton,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  BulkSendIcon,
  MeAndOthersIcon,
  OnlySignerIcon,
} from "global/components/RequestTypeIcons";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { CustomDialogComponent } from "global/components/Folders/CustomDialogComponent";
import { Folder, FolderItem, Path } from "global/components/Folders/types";
import { DateRange, SignRequest, SortingConfig } from "models/interfaces";
import strings from "global/constants/StringConstants";
import CustomDropdownButton from "global/components/CustomDropdownButton/CustomDropdownButton";
import {
  handleErrorMessage,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import { fetchFolderData as fetchTemplateFolderData } from "screens/Templates/MyTemplatesService";
import { hasAccessTo, hasWorkspacePermission } from "layouts/Config";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setReduxSelectedParticipant } from "redux/appSlice";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { usePopover } from "hooks/use-popover";
import {
  createSignFolder,
  deleteSignFolderAnyway,
  deleteSignFolderSafe,
  fetchSignFolderData,
  fetchSignFolderTree,
  moveSignFolder,
  renameSignFolder,
  searchSignRequestsAndFolders,
} from "../SignRequestFoldersService";
import { RequestMenuItem } from "./RequestActionsMenu";
import ShareRequestDialog from "./ShareRequestDialog";
import {
  getRequestMenu,
  getRequestMenuItems,
  renderDesktopSkeletons,
} from "../MySignRequestHelper";
import signRequestsStyles from "../SignRequests.styles";
import signRequestsTokens, {
  signRequestsResponsiveTokens,
} from "../SignRequests.tokens";
import { useSetPageHeader } from "global/components/PageHeader/PageHeaderContext";
import SentFolderRow from "./SentFolderRow";
import SentRequestRow from "./SentRequestRow";
import { SignRequestItemMenu } from "./SignRequestItemMenu";
import SignRequestsToolbar from "./SignRequestsToolbar";
import { getSignRequestsRowStatus } from "../SignRequestsRowHelper";
import SignRequestsFilterTabs, { BATCHES_TAB } from "./SignRequestsFilterTabs";
import SignRequestsFiltersToolbar from "./SignRequestsFiltersToolbar";
import SignRequestsFilterBar from "./SignRequestsFilterBar";
import SignRequestsSharedBanner from "./SignRequestsSharedBanner";
import SignRequestsFilterDrawer from "./SignRequestsFilterDrawer";
import { ManageViewsDialog, SaveViewDialog } from "./SignRequestsViewDialogs";
import ManageTagsDialog from "./ManageTagsDialog";
import RequestTagPicker from "./RequestTagPicker";
import filtersStyles, {
  SignRequestsTableColumn,
} from "../SignRequestsFilters.styles";
import {
  SIGN_REQUESTS_TAG_COLOR_KEYS,
  SignRequestsTagColor,
} from "../SignRequestsFilters.tokens";
import {
  ALL_REQUESTS_VIEW_ID,
  BATCHES_VIEW_ID,
  EMPTY_SIGN_REQUESTS_FILTERS,
  fromFilterBody,
  getFilterChips,
  getFilterErrorReason,
  getRequestTags,
  getSortLabel,
  hasAppliedFilters,
  isTagLimitReached,
  readSharedFilters,
  SIGN_REQUESTS_BUILT_IN_VIEWS,
  SIGN_REQUESTS_FILTER_CATEGORIES,
  SignRequestsFilterKey,
  SignRequestsFilterLookups,
  SignRequestsFilters,
  SignRequestsOption,
  SignRequestsSortKey,
  SignRequestsView,
  SignRequestTag,
  tagFromApi,
  toFilterBody,
  toggleValue,
  toTagHex,
  viewFromApi,
  withRequestTags,
} from "../SignRequestsFiltersHelper";
import {
  createSignRequestTag,
  createSignRequestView,
  deleteSignRequestTag,
  deleteSignRequestView,
  fetchFilteredSignRequests,
  fetchLastUsedSignRequestFilters,
  fetchSignRequestSenders,
  fetchSignRequestTags,
  fetchSignRequestViews,
  renameSignRequestView,
  saveLastUsedSignRequestFilters,
  setSignRequestTags,
  updateSignRequestTag,
} from "../SignRequestFiltersService";
import BatchListView from "screens/BulkSend/components/BatchListView";
import {
  ChevronRightGlyph,
  FolderPlusGlyph,
  HomeGlyph,
  PlusCircleGlyph,
} from "./SignRequestsIcons";
import { useFeature } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import { addSigner } from "screens/SignRequest/SignRequestHelper";
import { selectEmail, selectWorkspacePermissions } from "redux/authSlice";
import { RootState } from "utils/store";
import EmptyState from "global/components/EmptyState/EmptyState";
import SentRequestRowSkeleton from "./SentRequestRowSkeleton";
import { tourAttr } from "tour";

const styles = signRequestsStyles;
const tokens = signRequestsTokens;
const { phone } = signRequestsResponsiveTokens;

/** Header labels, in the row cells' own source order. */
const TABLE_COLUMNS: { key: SignRequestsTableColumn; label: string }[] = [
  { key: "subject", label: "Subject" },
  { key: "signers", label: "Signers" },
  { key: "sender", label: "Sender" },
  { key: "status", label: "Status" },
  { key: "tags", label: "Tags" },
  { key: "created", label: "Created" },
  { key: "expires", label: "Expires" },
  { key: "actions", label: "" },
];

const TEMPLATE_SORT: SortingConfig[] = [
  { entity: "template", order: "DESC", field: "createdOn" },
];

/** Status the folder endpoint reads as "every status". */
const ALL_STATUSES = "All";
const HOME_PATH: Path[] = [{ id: 0, name: "Home" }];

/**
 * A rejected tag or view change says why in its message ("a tag called
 * 'Urgent' already exists"); the generic handler would flatten that to "check
 * your request", so the reason is shown when there is one.
 */
const showFilterError = (error: any) => {
  const reason = getFilterErrorReason(error);
  if (reason) openErrorNotification(reason);
  else handleErrorMessage(error);
};

const folderPopoverMenuItems = () => {
  const canUpdate = hasAccessTo(
    strings.resources.sign,
    strings.updatePermission,
  );
  const canDelete = hasAccessTo(
    strings.resources.sign,
    strings.deletePermission,
  );
  const canAdd =
    hasAccessTo(strings.resources.sign, strings.addPermission) &&
    hasWorkspacePermission("document.create");
  return [
    ...(canUpdate ? [strings.rename] : []),
    ...(canDelete ? [strings.deleteFolder] : []),
    ...(canUpdate ? [strings.moveFolder] : []),
    ...(canAdd ? [strings.newSubfolder] : []),
  ];
};

interface SentRequestsFolderViewProps {
  loader: boolean;
  setLoader: (value: boolean) => void;
}

const SentRequestsFolderView = ({
  loader,
  setLoader,
}: SentRequestsFolderViewProps) => {
  const dispatch = useAppDispatch();
  const popover = usePopover<HTMLButtonElement>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [shareTarget, setShareTarget] = useState<{
    id: string;
    title?: string;
  } | null>(null);
  const [requestMenuItems, setRequestMenuItems] = useState<RequestMenuItem[]>(
    [],
  );

  const [data, setData] = useState<FolderItem[]>([]);
  // Infinite scroll runs off "is there another page?" rather than a total count,
  // so the list needs no count API — a page that comes back short of `perPage`
  // is the last one.
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
  const [parentId, setParentId] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [perPage] = useState<number>(10);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const [sortConfig] = useState<SortingConfig[]>([
    { entity: "signRequest", order: "DESC", field: "createdOn" },
  ]);
  /*
    A page that links here can say which tab it came from — the batch page
    does, so its back arrow returns to Batches rather than dropping the user
    on "All" with no idea where their list went.
  */
  const [status, setStatus] = useState<string>(
    (history.location.state as { tab?: string } | undefined)?.tab ?? "All",
  );
  /*
    Filters, tags and saved views are one plan feature. An account without it
    keeps the search / date / status-pill row it always had; everything below
    this line only comes into play with the feature on.
  */
  const hasAdvancedFilters = useFeature(FeatureKeys.ENVELOPE_ADVANCED_FILTERS);
  /*
    Filters can arrive on the URL — that is how a filtered list is handed to
    someone else — and those win over the user's last-used ones. Read once:
    the list owns them from then on.
  */
  const sharedQuery = useMemo(
    () => readSharedFilters(history.location.search),
    [],
  );
  const [filters, setFilters] = useState<SignRequestsFilters>(
    sharedQuery?.filters ?? EMPTY_SIGN_REQUESTS_FILTERS,
  );
  const [sort, setSort] = useState<SignRequestsSortKey>(
    sharedQuery?.sort ?? "newest",
  );
  const [isShared, setIsShared] = useState<boolean>(Boolean(sharedQuery));
  const [activeViewId, setActiveViewId] = useState<string | null>(() => {
    const tab = (history.location.state as { tab?: string } | undefined)?.tab;
    if (tab === BATCHES_TAB) return BATCHES_VIEW_ID;
    return sharedQuery ? sharedQuery.viewId : ALL_REQUESTS_VIEW_ID;
  });
  const [savedViews, setSavedViews] = useState<SignRequestsView[]>([]);
  const [tags, setTags] = useState<SignRequestTag[]>([]);
  const [senderOptions, setSenderOptions] = useState<SignRequestsOption[]>([]);
  const [templateOptions, setTemplateOptions] = useState<SignRequestsOption[]>(
    [],
  );
  /**
   * Whether the filtered endpoint honours `parentId`. It echoes the folder it
   * scoped to; a response without that echo came from a backend that ignored
   * it. `null` until the first browsing response says which.
   */
  const [supportsFolderScope, setSupportsFolderScope] = useState<
    boolean | null
  >(null);
  const folderScopeRef = useRef<boolean | null>(null);
  /** Folders of the folder being browsed, when the endpoint sends them. */
  const [responseFolders, setResponseFolders] = useState<FolderItem[] | null>(
    null,
  );
  /** Requests matching the applied filters, across every page. */
  const [total, setTotal] = useState<number | null>(null);
  const [filterDrawer, setFilterDrawer] = useState<{
    open: boolean;
    /** The category to open on, when something other than the first. */
    category?: SignRequestsFilterKey;
  }>({ open: false });
  const [viewDialog, setViewDialog] = useState<{
    view: SignRequestsView | null;
  } | null>(null);
  const [isSavingView, setIsSavingView] = useState<boolean>(false);
  const [isManageViewsOpen, setIsManageViewsOpen] = useState<boolean>(false);
  const [manageTags, setManageTags] = useState<{
    startWithNewTag: boolean;
  } | null>(null);
  const [tagPicker, setTagPicker] = useState<{
    anchor: HTMLElement;
    requestId: string;
  } | null>(null);
  /** The tag list is being read — the dialogs and the drawer wait on it. */
  const [isLoadingTags, setIsLoadingTags] = useState<boolean>(false);
  /** Sender / template / view lists still on their way, counted per request. */
  const [pendingOptionLoads, setPendingOptionLoads] = useState<number>(0);
  /** A row's tag set is being saved from the picker. */
  const [isSavingTags, setIsSavingTags] = useState<boolean>(false);
  /** Held back until the first load, so restoring filters is not a "change". */
  const isLastUsedReady = useRef<boolean>(false);

  /**
   * Which list is on screen. The filtered endpoint is flat — requests only,
   * across every folder — so with the feature on that is the list, and folder
   * browsing and bulk batches are views chosen from the Views menu. Without
   * it, the folder list is the list and "Batches" is one of its pills.
   */
  /*
    With no filter applied the list is browsed by folder, as it always was; a
    filter cuts across every folder, so folders step aside while one is on.
  */
  const isBrowsing = !hasAppliedFilters(filters);
  const listMode: "flat" | "folders" | "batches" = !hasAdvancedFilters
    ? status === BATCHES_TAB
      ? "batches"
      : "folders"
    : activeViewId === BATCHES_VIEW_ID
      ? "batches"
      : isBrowsing && parentId !== 0 && supportsFolderScope === false
        ? // The filtered endpoint cannot scope to a folder yet, so the inside
          // of one is still read from the folder endpoint.
          "folders"
        : "flat";
  /** Breadcrumb, folder rows and "Create Folder" belong to folder browsing. */
  const showFolders =
    listMode === "folders" || (listMode === "flat" && isBrowsing);
  /** The pills only exist without the feature; with it, every status shows. */
  const folderStatus = hasAdvancedFilters ? ALL_STATUSES : status;
  const views = useMemo(
    () => [...SIGN_REQUESTS_BUILT_IN_VIEWS, ...savedViews],
    [savedViews],
  );
  const filterLookups: SignRequestsFilterLookups = useMemo(
    () => ({ tags, senders: senderOptions, templates: templateOptions }),
    [tags, senderOptions, templateOptions],
  );
  const filterChips = getFilterChips(filters, filterLookups);
  /*
    Someone limited to their own requests gets a single sender back —
    themselves — and a filter with one choice is not a filter.
  */
  const filterCategories = useMemo(
    () =>
      SIGN_REQUESTS_FILTER_CATEGORIES.filter(
        (category) => category.key !== "senders" || senderOptions.length > 1,
      ),
    [senderOptions],
  );

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<any>("");
  const [newFolderName, setNewFolderName] = useState<string>("");
  const [folderNameError, setFolderNameError] = useState<string>("");
  const [selectedFolder, setSelectedFolder] = useState<FolderItem | null>(null);
  const [destFolder, setDestFolder] = useState<any | null>();
  const [folderTree, setFolderTree] = useState<Folder[]>([]);
  const [path, setPath] = useState<Path[]>(HOME_PATH);

  const [searchText, setSearchText] = useState<string>("");
  const [searchResults, setSearchResults] = useState<FolderItem[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const searchAnchorRef = useRef<HTMLDivElement | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    fromDate: null,
    toDate: null,
  });
  const [openDateRangePicker, setOpenDateRangePicker] =
    useState<boolean>(false);
  const canBulkSend = useFeature(FeatureKeys.ENVELOPE_BULK_SEND);
  const userEmail: string = useAppSelector(selectEmail);
  const { userName } = useAppSelector((state: RootState) => state.auth);
  // Subscribe so Create/Share/Void gating re-renders when workspace permissions load.
  useAppSelector(selectWorkspacePermissions);

  const folderIndex = useMemo(() => {
    const idx: Record<number, Folder> = {};
    const walk = (f: Folder) => {
      idx[f.folderId] = f;
      (f.subFolders || []).forEach(walk);
    };
    folderTree.forEach(walk);
    return idx;
  }, [folderTree]);

  const folderMenuItems = folderPopoverMenuItems();

  const hasSignFetchPermission = hasAccessTo(
    strings.resources.sign,
    strings.fetchPermission,
  );
  const canCreate =
    hasAccessTo(strings.resources.sign, strings.addPermission) &&
    hasWorkspacePermission("document.create");

  /**
   * The phone reference drops the `<h1>` and the breadcrumb from the page body
   * and carries the screen — or, once the user has drilled in, the open folder
   * — in the app bar instead, with a back arrow in place of the hamburger. The
   * tablet reference keeps both on the page, so the bar is only fed below `sm`.
   */
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const currentCrumb = path[path.length - 1];
  const isNested = showFolders && path.length > 1;
  /*
    Re-runs if the plan's features arrive after the first render and turn out
    not to include the filters: the screen then falls back to the folder list.
  */
  useEffect(() => {
    if (!hasSignFetchPermission) return;
    fetchFolderTreeData();
    if (hasAdvancedFilters) {
      loadAdvancedFilters();
    } else {
      fetchFoldersData(1, parentId, status, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAdvancedFilters]);

  useEffect(() => {
    if (pageNumber === 1) return;
    if (listMode === "flat") fetchFilteredPage(pageNumber);
    else appendNextPage(pageNumber, parentId, folderStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  /*
    Remembers the user's filters a second after they stop changing, so the next
    visit opens on them.
  */
  useEffect(() => {
    if (!hasAdvancedFilters || !isLastUsedReady.current) return;
    const timer = setTimeout(() => {
      // Best effort: a missed save only costs the next visit its restore.
      saveLastUsedSignRequestFilters(toFilterBody(filters, sort)).catch(
        () => undefined,
      );
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort]);

  /*
    Shortcuts from the filters spec: "/" opens the panel on the Signer field,
    "f" toggles the panel, Shift+X clears every filter. Esc is the drawer's and
    the dialogs' own. None of them fire while typing or with a dialog up.
  */
  useEffect(() => {
    if (!hasAdvancedFilters) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping =
        !!target &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));
      const hasDialog =
        Boolean(viewDialog) ||
        isManageViewsOpen ||
        Boolean(manageTags) ||
        Boolean(tagPicker) ||
        openDialog;
      if (isTyping || hasDialog || isLoading) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === "/") {
        event.preventDefault();
        setFilterDrawer({ open: true, category: "signer" });
      } else if (event.key.toLowerCase() === "f" && !event.shiftKey) {
        setFilterDrawer((previous) => ({ open: !previous.open }));
      } else if (event.key.toLowerCase() === "x" && event.shiftKey) {
        if (hasAppliedFilters(filters)) handleClearFilters();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  useEffect(() => {
    return () => {
      searchAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const trimmed = searchText.trim();
    if (!trimmed) {
      searchAbortRef.current?.abort();
      searchAbortRef.current = null;
      setSearchResults([]);
      setIsSearching(false);
      setSearchOpen(false);
      return;
    }
    const timer = setTimeout(async () => {
      searchAbortRef.current?.abort();
      const controller = new AbortController();
      searchAbortRef.current = controller;
      setIsSearching(true);
      try {
        const response = await searchSignRequestsAndFolders(
          trimmed,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setSearchResults(Array.isArray(response) ? response : []);
        setSearchOpen(true);
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          setSearchResults([]);
          setSearchOpen(true);
        }
      } finally {
        if (!controller.signal.aborted) setIsSearching(false);
      }
    }, strings.SEARCH_TIME_OUT);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Ask for the next page only while one is still expected; the fetch that
        // returns a short page flips `hasMore` off and stops the scroll.
        if (entries[0].isIntersecting && !isLoading && hasMore) {
          setPageNumber((prev) => prev + 1);
        }
      },
      { threshold: 1.0 },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }
    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [isLoading, hasMore, pageNumber]);

  const handleRequestTypeSelect = (type: string) => {
    if (type === "signer") {
      const selfSigner = {
        id: 0,
        name: userName,
        email: userEmail,
        order: 0,
        color: "",
        cc: false,
      };
      addSigner(selfSigner, [], dispatch, false);
      dispatch(setReduxSelectedParticipant(selfSigner));
      // Same folder hand-off as the request builder below.
      history.push({
        pathname: viewpaths.createSelfSignRequestPath,
        state: { parentId },
      });
      return;
    }

    // Carry the folder the user is standing in through to the builder, so the
    // request it sends is saved under that folder instead of the root. Router
    // state is the only channel that survives: SignRequestComponent clears the
    // redux request on mount before it reads the parent. `parentId` is 0 at the
    // root, which is exactly what the backend already treats as "no folder".
    history.push({
      pathname: viewpaths.createSignRequestsViewPath,
      search: `?type=${type}`,
      state: {
        isFileUploaded: false,
        parentId: showFolders ? parentId : 0,
      },
    });
  };

  const fetchFolderTreeData = async () => {
    try {
      const tree = await fetchSignFolderTree();
      setFolderTree(tree);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  /**
   * Loads the first page for a folder / status / filter. No count API is
   * called — the returned page size tells us whether more pages exist.
   */
  const fetchFoldersData = async (
    page: number,
    currentParentId: number,
    currentStatus: string,
    // Set by a caller that is switching lists, where a fetch already in flight
    // belongs to the list being left and must not block this one.
    force: boolean = false,
  ) => {
    if (isLoading && !force) return;
    try {
      setIsLoading(true);
      const response = await fetchSignFolderData(
        page,
        currentParentId,
        currentStatus,
        sortConfig,
        searchText,
        dateRange,
      );
      const rows = response || [];
      setData(rows);
      setHasMore(rows.length >= perPage);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const appendNextPage = async (
    page: number,
    currentParentId: number,
    currentStatus: string,
  ) => {
    try {
      setIsLoading(true);
      const response = await fetchSignFolderData(
        page,
        currentParentId,
        currentStatus,
        sortConfig,
        searchText,
        dateRange,
      );
      const rows = response || [];
      setData((prev) => [...prev, ...rows]);
      setHasMore(rows.length >= perPage);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToFolder = async (targetParentId: number, newPath: Path[]) => {
    setData([]);
    /*
      The folders the last response sent belong to the folder being left.
      Dropping them hands the rows to the folder tree, so the target's own
      sub-folders show at once while its requests load.
    */
    setResponseFolders(null);
    setPath(newPath);
    setParentId(targetParentId);
    setPageNumber(1);
    if (!hasAdvancedFilters) {
      await fetchFoldersData(1, targetParentId, folderStatus);
      return;
    }
    // Opening a folder is browsing: it leaves the batch list and drops any
    // filters, which would otherwise hide the folder that was just opened.
    setFilters(EMPTY_SIGN_REQUESTS_FILTERS);
    setIsShared(false);
    setActiveViewId(sort === "newest" ? ALL_REQUESTS_VIEW_ID : null);
    await fetchFilteredPage(
      1,
      EMPTY_SIGN_REQUESTS_FILTERS,
      sort,
      targetParentId,
    );
  };

  /**
   * One page of the flat, filtered list: page 1 replaces the rows, later pages
   * append. The response carries the total, so "is there another page?" is
   * answered exactly rather than guessed from a short page. The filters and
   * sort are parameters, defaulting to the applied ones, because a caller that
   * has just changed them cannot wait for state.
   */
  const fetchFilteredPage = async (
    page: number,
    nextFilters: SignRequestsFilters = filters,
    nextSort: SignRequestsSortKey = sort,
    nextParentId: number = parentId,
  ) => {
    const browsing = !hasAppliedFilters(nextFilters);
    // A new list: the old total no longer describes it.
    if (page === 1) setTotal(null);
    /*
      Inside a folder, on a backend whose filtered endpoint does not scope by
      folder yet: asking it would list every request in the account, so that
      folder is read from the folder endpoint instead.
    */
    const readFromFolderEndpoint = () =>
      page === 1
        ? fetchFoldersData(1, nextParentId, ALL_STATUSES, true)
        : appendNextPage(page, nextParentId, ALL_STATUSES);
    if (browsing && nextParentId !== 0 && folderScopeRef.current === false) {
      await readFromFolderEndpoint();
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetchFilteredSignRequests(page, {
        ...toFilterBody(nextFilters, nextSort),
        ...(browsing ? { parentId: nextParentId } : {}),
      });
      if (browsing) {
        const isScoped = typeof response?.parentId === "number";
        folderScopeRef.current = isScoped;
        setSupportsFolderScope(isScoped);
        if (!isScoped && nextParentId !== 0) {
          await readFromFolderEndpoint();
          return;
        }
      }
      const rows: FolderItem[] = response?.items ?? [];
      const nextTotal: number = response?.total ?? 0;
      const pageSize: number = response?.pageSize || perPage;
      setTotal(nextTotal);
      if (page === 1) {
        setResponseFolders(
          browsing && Array.isArray(response?.folders)
            ? response.folders
            : null,
        );
      }
      setData((previous) => (page === 1 ? rows : [...previous, ...rows]));
      setHasMore(rows.length > 0 && page * pageSize < nextTotal);
    } catch (error: any) {
      // Stop the scroll sentinel asking for a page that just failed.
      setHasMore(false);
      showFilterError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      setIsLoadingTags(true);
      const response = await fetchSignRequestTags();
      setTags((Array.isArray(response) ? response : []).map(tagFromApi));
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoadingTags(false);
    }
  };

  /** Everything the drawer and the Views menu choose from. */
  const loadFilterOptions = () => {
    const load = async (
      request: () => Promise<any>,
      apply: (rows: any[]) => void,
    ) => {
      try {
        setPendingOptionLoads((count) => count + 1);
        const response = await request();
        apply(Array.isArray(response) ? response : []);
      } catch (error: any) {
        handleErrorMessage(error);
      } finally {
        setPendingOptionLoads((count) => count - 1);
      }
    };

    loadTags();
    load(fetchSignRequestViews, (rows) => setSavedViews(rows.map(viewFromApi)));
    load(fetchSignRequestSenders, (rows) =>
      setSenderOptions(
        rows.map((sender) => ({
          value: sender.email,
          label: sender.name || sender.email,
        })),
      ),
    );
    // There is no flat template list; like the bulk-send picker, this offers
    // the first page of templates at the root of the library.
    load(
      () => fetchTemplateFolderData(1, 0, TEMPLATE_SORT),
      (rows) =>
        setTemplateOptions(
          rows
            .filter((row) => row?.type !== strings.folder && row?.id)
            .map((row) => ({ value: row.id, label: row.templateName })),
        ),
    );
  };

  /**
   * First load with the feature on. Which filters to open with, in order: the
   * URL's, then the user's last-used ones, then none.
   */
  const loadAdvancedFilters = async () => {
    // Raised before the first request, not by the list fetch further down:
    // the last-used lookup comes first, and without this the list would sit
    // on its empty state until that answered.
    setIsLoading(true);
    loadFilterOptions();

    let initialFilters = filters;
    let initialSort = sort;
    if (!sharedQuery) {
      try {
        const lastUsed = await fetchLastUsedSignRequestFilters();
        if (lastUsed?.filters) {
          const restored = fromFilterBody(lastUsed.filters);
          initialFilters = restored.filters;
          initialSort = restored.sort;
          setFilters(initialFilters);
          setSort(initialSort);
          if (hasAppliedFilters(initialFilters) || initialSort !== "newest") {
            setActiveViewId((current) =>
              current === ALL_REQUESTS_VIEW_ID ? null : current,
            );
          }
        }
      } catch (error: any) {
        // Nothing to restore is not worth a toast; the list opens unfiltered.
      }
    }

    if (activeViewId !== BATCHES_VIEW_ID) {
      await fetchFilteredPage(1, initialFilters, initialSort);
    } else {
      // The batch list loads and shows its own progress.
      setIsLoading(false);
    }
    isLastUsedReady.current = true;
  };

  /**
   * Applies a filter / sort combination to the flat list and reloads its first
   * page. `viewId` is the view the combination belongs to, or `null` once it
   * has been edited away from one.
   */
  const applyQuery = async (
    nextFilters: SignRequestsFilters,
    nextSort: SignRequestsSortKey,
    viewId: string | null,
    // A change that is not a new set of filters — a sort order — stays in the
    // folder being browsed; anything else starts again from the root.
    nextParentId: number = 0,
  ) => {
    setFilters(nextFilters);
    setSort(nextSort);
    setActiveViewId(viewId);
    setData([]);
    setPageNumber(1);
    if (nextParentId !== parentId) {
      setPath(HOME_PATH);
      setParentId(nextParentId);
    }
    await fetchFilteredPage(1, nextFilters, nextSort, nextParentId);
  };

  const handleApplyFilters = async (nextFilters: SignRequestsFilters) => {
    setFilterDrawer({ open: false });
    setIsShared(false);
    await applyQuery(nextFilters, sort, null);
  };

  const handleClearFilters = async () => {
    setIsShared(false);
    await applyQuery(
      EMPTY_SIGN_REQUESTS_FILTERS,
      sort,
      sort === "newest" ? ALL_REQUESTS_VIEW_ID : null,
    );
  };

  /** A new sort order keeps the active view only if it is that view's own. */
  const handleSortChange = (nextSort: SignRequestsSortKey) => {
    const activeView =
      listMode === "flat"
        ? views.find((view) => view.id === activeViewId)
        : undefined;
    return applyQuery(
      filters,
      nextSort,
      activeView && activeView.sort === nextSort ? activeView.id : null,
      listMode === "batches" ? 0 : parentId,
    );
  };

  const handleSelectView = async (view: SignRequestsView) => {
    setIsShared(false);
    // Batches are their own list, not a filter any endpoint here can apply.
    if (view.id === BATCHES_VIEW_ID) {
      setActiveViewId(BATCHES_VIEW_ID);
      setData([]);
      setPageNumber(1);
      return;
    }
    await applyQuery({ ...view.filters }, view.sort, view.id);
  };

  /** Saves the applied filters as a new view, or renames an existing one. */
  const handleSubmitView = async (name: string) => {
    const target = viewDialog?.view;
    try {
      setIsSavingView(true);
      if (target) {
        await renameSignRequestView(target.id, name);
        setSavedViews((previous) =>
          previous.map((view) =>
            view.id === target.id ? { ...view, name } : view,
          ),
        );
        openSuccessNotification("View renamed");
      } else {
        const created = viewFromApi(
          await createSignRequestView(name, toFilterBody(filters, sort)),
        );
        setSavedViews((previous) => [...previous, created]);
        setActiveViewId(created.id);
        setIsShared(false);
        openSuccessNotification("View saved");
      }
      setViewDialog(null);
    } catch (error: any) {
      showFilterError(error);
    } finally {
      setIsSavingView(false);
    }
  };

  const handleDeleteView = async (target: SignRequestsView) => {
    try {
      setIsActionLoading(true);
      await deleteSignRequestView(target.id);
      setSavedViews((previous) =>
        previous.filter((view) => view.id !== target.id),
      );
      if (activeViewId === target.id) setActiveViewId(null);
      openSuccessNotification("View deleted");
    } catch (error: any) {
      showFilterError(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const createTag = async (
    name: string,
    color: SignRequestsTagColor,
  ): Promise<SignRequestTag | null> => {
    try {
      const created = tagFromApi(
        await createSignRequestTag(name, toTagHex(color)),
      );
      // Re-read rather than append: only the list carries `canManage`.
      await loadTags();
      return created;
    } catch (error: any) {
      showFilterError(error);
      return null;
    }
  };

  const handleCreateTag = async (name: string, color: SignRequestsTagColor) => {
    const created = await createTag(name, color);
    if (created) openSuccessNotification("Tag created");
    return Boolean(created);
  };

  const handleUpdateTag = async (
    id: string,
    name: string,
    color: SignRequestsTagColor,
  ) => {
    try {
      await updateSignRequestTag(id, name, toTagHex(color));
      await loadTags();
      // Rows carry their own copy of each tag, so they are re-read too.
      if (listMode === "flat") await refreshCurrent();
      openSuccessNotification("Tag updated");
      return true;
    } catch (error: any) {
      showFilterError(error);
      return false;
    }
  };

  /** A deleted tag comes off every request, and out of the applied filters. */
  const handleDeleteTag = async (target: SignRequestTag) => {
    try {
      await deleteSignRequestTag(target.id);
      await loadTags();
      setSavedViews((previous) =>
        previous.map((view) => ({
          ...view,
          filters: {
            ...view.filters,
            tags: view.filters.tags.filter((id) => id !== target.id),
          },
        })),
      );
      if (filters.tags.includes(target.id)) {
        await applyQuery(
          { ...filters, tags: filters.tags.filter((id) => id !== target.id) },
          sort,
          null,
        );
      } else if (listMode === "flat") {
        await refreshCurrent();
      }
      openSuccessNotification("Tag deleted");
      return true;
    } catch (error: any) {
      showFilterError(error);
      return false;
    }
  };

  /** Replaces one request's tag set and shows what the API kept. */
  const saveRequestTags = async (requestId: string, tagIds: string[]) => {
    try {
      setIsSavingTags(true);
      const saved = await setSignRequestTags(requestId, tagIds);
      setData((previous) =>
        previous.map((item) =>
          item.id === requestId
            ? withRequestTags(item, Array.isArray(saved) ? saved : [])
            : item,
        ),
      );
    } catch (error: any) {
      showFilterError(error);
    } finally {
      setIsSavingTags(false);
    }
  };

  /** "Create tag …" in a row's picker: makes the tag and puts it on the row. */
  const handleCreateTagForRequest = async (name: string) => {
    if (!tagPicker) return;
    const { requestId } = tagPicker;
    const currentIds = tagPickerIds;
    // Raised here too, so the picker stays busy across both requests.
    setIsSavingTags(true);
    const created = await createTag(
      name,
      SIGN_REQUESTS_TAG_COLOR_KEYS[
        tags.length % SIGN_REQUESTS_TAG_COLOR_KEYS.length
      ],
    );
    if (created && !isTagLimitReached(currentIds)) {
      await saveRequestTags(requestId, [...currentIds, created.id]);
    } else {
      setIsSavingTags(false);
    }
  };

  const openManageTags = (startWithNewTag: boolean) => {
    setTagPicker(null);
    setManageTags({ startWithNewTag });
    // Usage counts move every time a row's tags change; read them fresh.
    loadTags();
  };

  const handleFolderClick = async (item: FolderItem) => {
    const newPath = [
      ...path,
      { id: item.autoGeneratedId, name: item.templateName },
    ];
    await navigateToFolder(item.autoGeneratedId, newPath);
  };

  const handleBreadcrumbClick = async (index: number) => {
    if (index === path.length - 1) return;
    const newPath = path.slice(0, index + 1);
    const targetParentId = newPath[newPath.length - 1].id;
    await navigateToFolder(targetParentId, newPath);
  };

  const handleHomeClick = async () => {
    await navigateToFolder(0, HOME_PATH);
  };

  useSetPageHeader(
    isPhone ? (isNested ? currentCrumb.name : "Sign Requests") : undefined,
    isPhone && isNested
      ? () => handleBreadcrumbClick(path.length - 2)
      : undefined,
  );

  const buildPathToFolder = (targetFolderId: number): Path[] => {
    const home: Path = { id: 0, name: "Home" };
    if (!targetFolderId) return [home];
    const target = folderIndex[targetFolderId];
    if (!target) return [home];
    const ids = (target.path ?? "")
      .split(".")
      .map(Number)
      .filter((n) => !Number.isNaN(n));
    return [
      home,
      ...ids.map((id) => ({
        id,
        name: folderIndex[id]?.name ?? "…",
      })),
    ];
  };

  const resolveAncestorCrumbs = (item: FolderItem): string[] => {
    const ids = (item.path ?? "")
      .split(".")
      .map(Number)
      .filter((n) => !Number.isNaN(n));
    const ancestorIds = ids.slice(0, -1);
    return ["Home", ...ancestorIds.map((id) => folderIndex[id]?.name ?? "…")];
  };

  const handleSearchSelect = async (item: FolderItem) => {
    setSearchOpen(false);
    const isFolder = item.type === strings.folder;
    if (isFolder) {
      const newPath = [
        ...buildPathToFolder(item.parentId || 0),
        { id: item.autoGeneratedId, name: item.templateName ?? "" },
      ];
      await navigateToFolder(item.autoGeneratedId, newPath);
    } else if (item.id) {
      history.push(`${viewpaths.viewRequestViewPath}/${item.id}`);
    }
  };

  const handleStatusChange = async (value: string) => {
    setStatus(value);
    setData([]);
    setPageNumber(1);
    // Batches are their own list, not a status the folder API can filter by.
    if (value === BATCHES_TAB) return;
    await fetchFoldersData(1, parentId, value);
  };

  const refreshCurrent = async () => {
    setData([]);
    setPageNumber(1);
    if (listMode === "flat") await fetchFilteredPage(1);
    else await fetchFoldersData(1, parentId, folderStatus);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setFolderNameError("");
    setNewFolderName("");
    setDestFolder(null);
  };

  const handleOpenFolderClick = () => {
    setSelectedFolder(null);
    setDialogType(strings.createNewFolder);
    setNewFolderName("");
    setOpenDialog(true);
  };

  const handleMoveRequest = (request: SignRequest) => {
    setSelectedFolder(request as unknown as FolderItem);
    setDestFolder(null);
    setDialogType(strings.moveFolder);
    setOpenDialog(true);
  };

  const handleShareRequest = (request: SignRequest) => {
    setShareTarget({ id: request.id, title: request.emailSubject });
  };

  const createNewFolderFunction = async (parentFolderId: number) => {
    const trimmed = newFolderName.trim();
    if (!trimmed) {
      setFolderNameError("Please enter folder name");
      return;
    }
    if (/[^a-zA-Z0-9_\- ]/.test(trimmed)) {
      setFolderNameError("Only letters, numbers, spaces, - and _ are allowed");
      return;
    }
    setFolderNameError("");
    try {
      setOpenDialog(false);
      setIsActionLoading(true);
      await createSignFolder(trimmed, parentFolderId);
      setNewFolderName("");
      openSuccessNotification("Folder created successfully!");
      await fetchFolderTreeData();
      await refreshCurrent();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const renameFolderFunction = async () => {
    const trimmed = newFolderName.trim();
    if (!trimmed) {
      setFolderNameError("Please enter folder name");
      return;
    }
    if (/[^a-zA-Z0-9_\- ]/.test(trimmed)) {
      setFolderNameError("Only letters, numbers, spaces, - and _ are allowed");
      return;
    }
    if (trimmed === selectedFolder?.templateName) {
      setFolderNameError("Please enter a different name");
      return;
    }
    setFolderNameError("");
    try {
      setOpenDialog(false);
      setIsActionLoading(true);
      await renameSignFolder(selectedFolder!.autoGeneratedId, trimmed);
      setNewFolderName("");
      setSelectedFolder(null);
      await fetchFolderTreeData();
      await refreshCurrent();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const moveFolders = async (id: number, dest: number) => {
    try {
      setOpenDialog(false);
      setIsActionLoading(true);
      await moveSignFolder(id, dest);
      setDestFolder(null);
      await fetchFolderTreeData();
      await refreshCurrent();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const deleteFolder = async (id: number) => {
    try {
      setOpenDialog(false);
      setIsActionLoading(true);
      await deleteSignFolderSafe(id);
      await fetchFolderTreeData();
      await refreshCurrent();
    } catch (error: any) {
      if (error?.code === 406) {
        setDialogType(strings.deleteAnyway);
        setOpenDialog(true);
        setIsActionLoading(true);
      } else {
        handleErrorMessage(error);
      }
    } finally {
      setIsActionLoading(false);
    }
  };

  const deleteAnyway = async (id: number) => {
    try {
      setIsActionLoading(true);
      setOpenDialog(false);
      await deleteSignFolderAnyway(id);
      await fetchFolderTreeData();
      await refreshCurrent();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  /**
   * The three request types, shared by the header's split button, the phone's
   * full-width one and the narrow phone column's floating pill, so none of the
   * three can drift from the others.
   */
  const requestTypeMenuItems = [
    {
      icon: <OnlySignerIcon sx={{ fontSize: 20, color: "text.secondary" }} />,
      label: "I am the only signer",
      onClick: () => handleRequestTypeSelect("signer"),
    },
    {
      icon: <MeAndOthersIcon sx={{ fontSize: 20, color: "text.secondary" }} />,
      label: "Me and others",
      onClick: () => handleRequestTypeSelect("others"),
    },
    {
      icon: (
        <BulkSendIcon
          sx={{
            fontSize: 20,
            color: canBulkSend ? "text.secondary" : "text.disabled",
          }}
        />
      ),
      label: "Bulk send",
      disabled: !canBulkSend,
      onClick: () => {
        if (!canBulkSend) return;
        history.push(viewpaths.bulkSendViewPath);
      },
    },
  ];

  const renderHeader = () => (
    <>
      <Box sx={styles.header}>
        <Box sx={styles.headerText}>
          <Typography component="h1" sx={styles.headerTitle}>
            Sign Requests
          </Typography>
          <Typography component="p" sx={styles.headerSubtitle}>
            {/*
              The count is the new list's own, so it shows only once that
              list's total is in. A list read from the folder endpoint has no
              total and keeps the default line.
            */}
            {listMode === "flat" && total !== null ? (
              `Showing ${data.length} of ${total}`
            ) : listMode === "flat" && isLoading ? (
              <Skeleton
                variant="text"
                aria-label="Loading count"
                sx={{
                  display: "inline-block",
                  width: tokens.header.countSkeletonWidth,
                }}
              />
            ) : (
              "Requests you've created and sent out — track every signer's progress."
            )}
          </Typography>
        </Box>
        {canCreate && (
          <Box sx={styles.headerActions}>
            {/* A folder can only be made where folders are listed. */}
            {showFolders && (
              <Button
                disableRipple
                onClick={handleOpenFolderClick}
                disabled={loader || isActionLoading}
                aria-label="Create folder"
                sx={styles.secondaryButton}
                {...tourAttr("requests.createFolder")}
              >
                <FolderPlusGlyph sx={styles.secondaryButtonGlyph} />
                {/* The tablet reference draws this as an icon-only square. */}
                <Box component="span" sx={styles.secondaryButtonLabel}>
                  Create Folder
                </Box>
              </Button>
            )}
            <Box {...tourAttr("requests.createRequest")}>
              <CustomDropdownButton
                handleMainClick={() => handleRequestTypeSelect("others")}
                buttonLabel="Create Request"
                startIcon={
                  <PlusCircleGlyph
                    sx={{
                      width: tokens.primaryButton.iconSize,
                      height: tokens.primaryButton.iconSize,
                    }}
                  />
                }
                menuItems={[
                  {
                    icon: (
                      <OnlySignerIcon
                        sx={{ fontSize: 20, color: "text.secondary" }}
                      />
                    ),
                    label: "I am the only signer",
                    onClick: () => handleRequestTypeSelect("signer"),
                  },
                  {
                    icon: (
                      <MeAndOthersIcon
                        sx={{ fontSize: 20, color: "text.secondary" }}
                      />
                    ),
                    label: "Me and others",
                    onClick: () => handleRequestTypeSelect("others"),
                  },
                  {
                    icon: (
                      <BulkSendIcon
                        sx={{
                          fontSize: 20,
                          color: canBulkSend
                            ? "text.secondary"
                            : "text.disabled",
                        }}
                      />
                    ),
                    label: "Bulk send",
                    disabled: !canBulkSend,
                    onClick: () => {
                      if (!canBulkSend) return;
                      history.push(viewpaths.bulkSendViewPath);
                    },
                  },
                ]}
                disabled={loader}
              />
            </Box>{" "}
          </Box>
        )}
      </Box>

      {/*
        Phone stand-in for the header actions: "Create Request" runs full width
        under the strapline, at every phone size. The 375 column of the
        reference floated a pill over the list instead, but it sat on top of
        the rows it was meant to sit beside, so the full-width button is used
        throughout.
      */}
      {canCreate && (
        /*
          Carries the same tour marker as the desktop button in
          `headerActions`, which is `display: none` below `sm`. Only one of the
          pair is ever visible and `resolveTargetElement` skips the hidden one,
          so the step lands on whichever is on screen.
        */
        <Box sx={styles.createMobile} {...tourAttr("requests.createRequest")}>
          <CustomDropdownButton
            handleMainClick={() => handleRequestTypeSelect("others")}
            buttonLabel="Create Request"
            buttonGroupParentSx={{ width: "100%" }}
            startIcon={
              <PlusCircleGlyph
                sx={{
                  width: phone.createButton.iconSize,
                  height: phone.createButton.iconSize,
                }}
              />
            }
            menuItems={requestTypeMenuItems}
            disabled={loader}
          />
        </Box>
      )}

      {showFolders && (
        <Box sx={styles.breadcrumb}>
          {path.map((crumb, index) => {
            const isLast = index === path.length - 1;
            const label =
              crumb.name.length > tokens.breadcrumb.maxLabelChars
                ? `${crumb.name.slice(0, tokens.breadcrumb.maxLabelChars)}…`
                : crumb.name;
            return (
              <Fragment key={crumb.id}>
                {index > 0 && (
                  <ChevronRightGlyph sx={styles.breadcrumbSeparator} />
                )}
                <Tooltip
                  title={crumb.name}
                  disableHoverListener={
                    crumb.name.length <= tokens.breadcrumb.maxLabelChars
                  }
                  arrow
                >
                  <Button
                    disableRipple
                    disabled={isLoading}
                    onClick={() =>
                      index === 0
                        ? handleHomeClick()
                        : handleBreadcrumbClick(index)
                    }
                    sx={styles.breadcrumbItem(isLast, isLoading)}
                  >
                    {index === 0 && <HomeGlyph sx={styles.breadcrumbGlyph} />}
                    {label}
                  </Button>
                </Tooltip>
              </Fragment>
            );
          })}
        </Box>
      )}
    </>
  );

  const handleFolderMenuItem = (item: FolderItem, label: string) => {
    setSelectedFolder(item);
    setDialogType(label);
    setNewFolderName(label === strings.rename ? (item.templateName ?? "") : "");
    setFolderNameError("");
    setOpenDialog(true);
  };

  const handleRequestMenu = (request: SignRequest, anchor: HTMLElement) => {
    setRequestMenuItems(
      getRequestMenuItems(
        request,
        dispatch,
        setLoader,
        refreshCurrent,
        handleMoveRequest,
        handleShareRequest,
      ),
    );
    setAnchorEl(anchor);
    popover.handleOpen();
  };

  const openRequest = (request: SignRequest) => {
    if (request.status === strings.draft) {
      history.push({
        pathname: `${viewpaths.editDraftViewPath}`,
        state: request,
      });
      return;
    }
    if (hasSignFetchPermission) {
      history.push(`${viewpaths.viewRequestViewPath}/${request.id}`);
    }
  };

  /*
    A search or a filter that matched nothing is not a first use: it drops the
    "yet", swaps the icon for the magnifier and offers the way back instead of
    the create action.
  */
  const renderEmptyState = () => {
    const query = searchText.trim();
    if (listMode === "folders" && query) {
      return (
        <EmptyState
          icon="search"
          title="No requests match your search"
          body={`Nothing in this folder matches "${query}". Clear the search to see everything here.`}
          primaryAction={{
            label: "Clear search",
            onClick: () => setSearchText(""),
          }}
        />
      );
    }
    if (listMode === "flat" && filterChips.length > 0) {
      return (
        <EmptyState
          icon="search"
          title="No sign requests match these filters"
          body="Remove a filter or clear them all to see more requests."
          primaryAction={{ label: "Clear all", onClick: handleClearFilters }}
        />
      );
    }
    return (
      <EmptyState
        icon="doc"
        title="No requests here yet"
        body={
          listMode === "flat"
            ? "Requests you send are listed here."
            : "Requests you send from this folder are listed here, newest first."
        }
      />
    );
  };

  /*
    Folders of the folder being browsed. The filtered endpoint lists requests
    only, so until it sends folders alongside them these come from the folder
    tree the screen already holds — name only, no count or date. The tree's
    top level is the root itself (`folderId` 0, named "home"), so the root's
    folders are its `subFolders`, not the tree array.
  */
  const browseFolders: FolderItem[] =
    listMode === "flat" && isBrowsing
      ? (responseFolders ??
        (
          folderIndex[parentId]?.subFolders ??
          (parentId === 0 ? folderTree : [])
        ).map(
          (folder) =>
            ({
              type: strings.folder,
              autoGeneratedId: folder.folderId,
              templateName: folder.name,
              path: folder.path,
              parentId: folder.parentId,
            }) as FolderItem,
        ))
      : [];

  /** Only the filtered list carries tags; the folder list drops the column. */
  const withTags = listMode === "flat";
  const tagPickerRequest = tagPicker
    ? data.find((item) => item.id === tagPicker.requestId)
    : undefined;
  const tagPickerIds = tagPickerRequest
    ? getRequestTags(tagPickerRequest).map((tag) => tag.id)
    : [];

  const renderRows = () => (
    <Box {...tourAttr("requests.list")} sx={styles.listCard}>
      {/*
        Visual column labels only. Each row already names its own contents for
        assistive tech, and a lone header row with no table around it would be
        orphaned ARIA.
      */}
      <Box aria-hidden="true" sx={filtersStyles.tableHead(withTags)}>
        {TABLE_COLUMNS.filter(
          (column) => withTags || column.key !== "tags",
        ).map((column) => (
          <Box
            key={column.key}
            component="span"
            sx={filtersStyles.tableHeadCell(column.key)}
          >
            {column.label}
          </Box>
        ))}
      </Box>
      {browseFolders.map((item) => (
        <SentFolderRow
          key={`browse-folder-${item.autoGeneratedId}`}
          item={item}
          isLoading={isActionLoading}
          menuItems={folderMenuItems}
          onOpen={handleFolderClick}
          onMenuItem={handleFolderMenuItem}
        />
      ))}
      {data.map((item) =>
        item.type === strings.folder ? (
          <SentFolderRow
            key={`folder-${item.autoGeneratedId}`}
            item={item}
            isLoading={isActionLoading}
            menuItems={folderMenuItems}
            onOpen={handleFolderClick}
            onMenuItem={handleFolderMenuItem}
          />
        ) : (
          <SentRequestRow
            key={item.id ?? `req-${item.autoGeneratedId}`}
            request={item as SignRequest}
            isLoading={isActionLoading}
            onOpen={openRequest}
            onMenu={handleRequestMenu}
            withTags={withTags}
            isTagPickerOpen={tagPicker?.requestId === item.id}
            onTag={(request, anchor) =>
              setTagPicker({ anchor, requestId: request.id })
            }
          />
        ),
      )}
      {getRequestMenu(
        {
          open: popover.open,
          anchorEl: anchorEl,
          setAnchorEl: setAnchorEl,
          onOpen: popover.handleOpen,
          onClose: popover.handleClose,
        },
        requestMenuItems,
      )}
      {isLoading &&
        Array.from({ length: 6 }, (_, index) => (
          <SentRequestRowSkeleton
            key={`request-skeleton-${index}`}
            withTags={withTags}
          />
        ))}
      {!isLoading &&
        data.length === 0 &&
        browseFolders.length === 0 &&
        renderEmptyState()}
    </Box>
  );

  /** The search field and its results dropdown — the same in both toolbars. */
  const searchProps = {
    searchText,
    onSearchChange: setSearchText,
    searchAnchorRef,
    searchOpen,
    setSearchOpen,
    isSearching,
    searchResults,
    resolveAncestorCrumbs,
    onSearchSelect: handleSearchSelect,
    disabled: isLoading,
    renderItemActions: (item: FolderItem) => (
      <SignRequestItemMenu
        item={item}
        isLoading={isActionLoading}
        folderMenuItems={folderMenuItems}
        onFolderMenuItem={handleFolderMenuItem}
        onRequestMenu={handleRequestMenu}
      />
    ),
    renderItemBadge: (item: FolderItem) => {
      // Folders have no status; requests show the same chip as the row.
      if (item.type === strings.folder) return null;
      const rowStatus = getSignRequestsRowStatus(
        item as unknown as SignRequest,
      );
      return (
        <Box sx={styles.statusChip(rowStatus.key)}>
          <Box component="span" sx={styles.statusDot(rowStatus.key)} />
          <Box component="span" sx={styles.statusLabel(rowStatus.key)}>
            {rowStatus.label}
          </Box>
        </Box>
      );
    },
  };

  return (
    <>
      {isActionLoading && (
        <Box
          sx={{
            // `inset: 0` rather than `100vw/100vh` — viewport units include
            // the scrollbar gutter.
            position: "fixed",
            inset: 0,
            bgcolor: "action.disabledBackground",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Box sx={styles.page}>
        {renderHeader()}
        <Box {...tourAttr("requests.filter")}>
          {hasAdvancedFilters ? (
            <SignRequestsFiltersToolbar
              {...searchProps}
              sort={sort}
              onSortChange={handleSortChange}
              views={views}
              activeViewId={activeViewId}
              isLoadingViews={pendingOptionLoads > 0}
              onSelectView={handleSelectView}
              onSaveView={() => setViewDialog({ view: null })}
              onRenameView={(view) => setViewDialog({ view })}
              onDeleteView={handleDeleteView}
              onManageViews={() => setIsManageViewsOpen(true)}
            />
          ) : (
            <>
              <SignRequestsToolbar
                {...searchProps}
                dateRange={dateRange}
                setDateRange={setDateRange}
                openDateRangePicker={openDateRangePicker}
                setOpenDateRangePicker={setOpenDateRangePicker}
                onDateApplied={refreshCurrent}
              />
              <SignRequestsFilterTabs
                value={status}
                disabled={isLoading}
                onChange={handleStatusChange}
              />
            </>
          )}
        </Box>
        {hasAdvancedFilters && (
          <>
            {isShared && (
              <SignRequestsSharedBanner
                onSave={() => setViewDialog({ view: null })}
                onDismiss={() => setIsShared(false)}
              />
            )}
            <SignRequestsFilterBar
              filters={filters}
              categories={filterCategories}
              lookups={filterLookups}
              disabled={isLoading}
              onApply={handleApplyFilters}
              onClearAll={handleClearFilters}
              onOpenAllFilters={() => setFilterDrawer({ open: true })}
              // Filters that belong to no view yet are the ones worth saving.
              canSaveView={!activeViewId && filterChips.length > 0}
              onSaveView={() => setViewDialog({ view: null })}
              isLoadingTags={isLoadingTags}
              isLoadingOptions={pendingOptionLoads > 0}
              onManageTags={() => openManageTags(false)}
              onCreateFirstTag={() => openManageTags(true)}
            />
          </>
        )}
        {listMode === "batches" ? (
          <Box sx={styles.listCard}>
            <BatchListView />
          </Box>
        ) : (
          <>
            {renderRows()}
            {/* The batch list pages itself, so the scroll sentinel sits with
                the envelope list it belongs to. */}
            <div ref={observerRef}></div>
          </>
        )}
      </Box>

      <CustomDialogComponent
        openDialog={openDialog}
        handleDialogClose={handleDialogClose}
        dialogType={dialogType}
        newFolderName={newFolderName}
        setNewFolderName={setNewFolderName}
        folderNameError={folderNameError}
        setFolderNameError={setFolderNameError}
        moveFolders={moveFolders}
        createNewFolderFunction={createNewFolderFunction}
        renameFolderFunction={renameFolderFunction}
        deleteFolder={deleteFolder}
        deleteAnyway={deleteAnyway}
        selectedFolder={selectedFolder}
        setSelectedFolder={setSelectedFolder}
        setDestFolder={setDestFolder}
        destFolder={destFolder}
        folderTree={folderTree}
        parentId={parentId}
      />
      <ShareRequestDialog
        request={shareTarget}
        onClose={() => setShareTarget(null)}
      />
      {hasAdvancedFilters && (
        <>
          <SignRequestsFilterDrawer
            open={filterDrawer.open}
            onClose={() => setFilterDrawer({ open: false })}
            filters={filters}
            onApply={handleApplyFilters}
            categories={filterCategories}
            lookups={filterLookups}
            initialCategory={filterDrawer.category}
            isLoadingTags={isLoadingTags}
            isLoadingOptions={pendingOptionLoads > 0}
            onManageTags={() => openManageTags(false)}
            onCreateFirstTag={() => openManageTags(true)}
          />
          <SaveViewDialog
            open={Boolean(viewDialog)}
            view={viewDialog?.view}
            summary={[
              ...filterChips,
              { key: "sort", label: "Sort", value: getSortLabel(sort) },
            ]}
            busy={isSavingView}
            onClose={() => setViewDialog(null)}
            onSubmit={handleSubmitView}
          />
          <ManageViewsDialog
            open={isManageViewsOpen}
            views={views}
            lookups={filterLookups}
            onClose={() => setIsManageViewsOpen(false)}
            onRename={(view) => {
              setIsManageViewsOpen(false);
              setViewDialog({ view });
            }}
            onDelete={handleDeleteView}
          />
          <ManageTagsDialog
            open={Boolean(manageTags)}
            tags={tags}
            startWithNewTag={manageTags?.startWithNewTag}
            loading={isLoadingTags}
            onClose={() => setManageTags(null)}
            onCreate={handleCreateTag}
            onUpdate={handleUpdateTag}
            onDelete={handleDeleteTag}
          />
          <RequestTagPicker
            anchorEl={tagPicker?.anchor ?? null}
            onClose={() => setTagPicker(null)}
            tags={tags}
            selected={tagPickerIds}
            loading={isLoadingTags}
            busy={isSavingTags}
            onToggle={(tagId) => {
              if (tagPicker) {
                saveRequestTags(
                  tagPicker.requestId,
                  toggleValue(tagPickerIds, tagId),
                );
              }
            }}
            onCreate={handleCreateTagForRequest}
            onManageTags={() => openManageTags(false)}
          />
        </>
      )}
    </>
  );
};

export default SentRequestsFolderView;
